import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";

export default function ComplianceGovernance() {
  const [meetings, setMeetings] = useState<any[]>([]);
  const [decisions, setDecisions] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [openD, setOpenD] = useState(false);
  const { toast } = useToast();
  const [m, setM] = useState({ committee: "Investment Committee", meeting_date: format(new Date(), "yyyy-MM-dd"), attendees: "", agenda: "", minutes: "" });
  const [d, setD] = useState({ meeting_id: "", decision_date: format(new Date(), "yyyy-MM-dd"), decision_text: "", owner: "", due_date: "" });

  const load = async () => {
    const [mr, dr] = await Promise.all([
      supabase.from("compliance_meetings").select("*").order("meeting_date", { ascending: false }),
      supabase.from("compliance_decisions").select("*").order("decision_date", { ascending: false }),
    ]);
    setMeetings(mr.data || []); setDecisions(dr.data || []);
  };
  useEffect(() => { load(); }, []);

  const saveMeeting = async () => {
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase.from("compliance_meetings").insert({
      committee: m.committee, meeting_date: m.meeting_date,
      attendees: m.attendees ? m.attendees.split(",").map(s => s.trim()) : [],
      agenda: m.agenda, minutes: m.minutes, created_by: u.user?.id,
    });
    if (error) toast({ title: "Failed", description: error.message, variant: "destructive" });
    else { setOpen(false); load(); }
  };

  const saveDecision = async () => {
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase.from("compliance_decisions").insert({
      meeting_id: d.meeting_id || null, decision_date: d.decision_date,
      decision_text: d.decision_text, owner: d.owner, due_date: d.due_date || null, created_by: u.user?.id,
    });
    if (error) toast({ title: "Failed", description: error.message, variant: "destructive" });
    else { setOpenD(false); load(); }
  };

  const setStatus = async (id: string, status: string) => {
    await supabase.from("compliance_decisions").update({ status }).eq("id", id);
    load();
  };

  return (
    <Tabs defaultValue="meetings" className="space-y-4">
      <TabsList><TabsTrigger value="meetings">Meeting Log</TabsTrigger><TabsTrigger value="decisions">Decisions Register</TabsTrigger></TabsList>

      <TabsContent value="meetings">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>IC / Risk Oversight Meetings</CardTitle>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button size="sm">New meeting</Button></DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>Log meeting</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Committee</Label>
                    <Select value={m.committee} onValueChange={(v) => setM({ ...m, committee: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{["Investment Committee","Risk Oversight Panel","Board"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Date</Label><Input type="date" value={m.meeting_date} onChange={(e) => setM({ ...m, meeting_date: e.target.value })} /></div>
                  <div><Label>Attendees (comma-separated)</Label><Input value={m.attendees} onChange={(e) => setM({ ...m, attendees: e.target.value })} /></div>
                  <div><Label>Agenda</Label><Textarea value={m.agenda} onChange={(e) => setM({ ...m, agenda: e.target.value })} /></div>
                  <div><Label>Minutes</Label><Textarea rows={6} value={m.minutes} onChange={(e) => setM({ ...m, minutes: e.target.value })} /></div>
                  <Button onClick={saveMeeting}>Save</Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {meetings.length === 0 ? <p className="text-muted-foreground text-sm">No meetings logged.</p> :
              <div className="space-y-3">
                {meetings.map((mt) => (
                  <div key={mt.id} className="border rounded p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div><strong>{mt.committee}</strong> — {format(new Date(mt.meeting_date), "dd/MM/yyyy")}</div>
                      <Badge variant="outline">{mt.attendees?.length ?? 0} attendees</Badge>
                    </div>
                    {mt.agenda && <p className="text-sm"><strong>Agenda:</strong> {mt.agenda}</p>}
                    {mt.minutes && <p className="text-sm mt-1 whitespace-pre-wrap">{mt.minutes}</p>}
                  </div>
                ))}
              </div>}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="decisions">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Decisions Register</CardTitle>
            <Dialog open={openD} onOpenChange={setOpenD}>
              <DialogTrigger asChild><Button size="sm">New decision</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Log decision</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Meeting (optional)</Label>
                    <Select value={d.meeting_id} onValueChange={(v) => setD({ ...d, meeting_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Standalone" /></SelectTrigger>
                      <SelectContent>{meetings.map((mt) => <SelectItem key={mt.id} value={mt.id}>{mt.committee} {format(new Date(mt.meeting_date), "dd/MM/yyyy")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Date</Label><Input type="date" value={d.decision_date} onChange={(e) => setD({ ...d, decision_date: e.target.value })} /></div>
                  <div><Label>Decision</Label><Textarea value={d.decision_text} onChange={(e) => setD({ ...d, decision_text: e.target.value })} /></div>
                  <div><Label>Owner</Label><Input value={d.owner} onChange={(e) => setD({ ...d, owner: e.target.value })} /></div>
                  <div><Label>Due date</Label><Input type="date" value={d.due_date} onChange={(e) => setD({ ...d, due_date: e.target.value })} /></div>
                  <Button onClick={saveDecision} disabled={!d.decision_text}>Save</Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {decisions.length === 0 ? <p className="text-muted-foreground text-sm">No decisions logged.</p> :
              <table className="w-full text-sm">
                <thead className="border-b"><tr><th className="text-left p-2">Date</th><th className="text-left p-2">Decision</th><th className="text-left p-2">Owner</th><th className="text-left p-2">Due</th><th className="text-left p-2">Status</th></tr></thead>
                <tbody>{decisions.map((dc) => (
                  <tr key={dc.id} className="border-b">
                    <td className="p-2">{format(new Date(dc.decision_date), "dd/MM/yyyy")}</td>
                    <td className="p-2">{dc.decision_text}</td>
                    <td className="p-2">{dc.owner ?? "—"}</td>
                    <td className="p-2">{dc.due_date ? format(new Date(dc.due_date), "dd/MM/yyyy") : "—"}</td>
                    <td className="p-2">
                      <Select value={dc.status} onValueChange={(v) => setStatus(dc.id, v)}>
                        <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>{["open","in_progress","closed"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}</tbody>
              </table>}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
